/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/attendance.ts
 * PURPOSE: Business logic service layer implementation
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: GPS-validated attendance with Vietnamese Labor Law compliance
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  */

import { apiRequest, apiV2 } from "./config";
import {
  AttendanceStatus,
  ClockRequest,
  ClockResponse,
} from "../../types/attendance";
import { GPSConfig } from "../../config/enums";

export const attendanceApi = {
  /**
   * Get current attendance status
   */
  async attendance_status_get__includes_predictions(
    token: string,
  ): Promise<AttendanceStatus> {
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    return apiRequest(`/attendance/status?t=${timestamp}`, {
      method: "GET",
      token,
    });
  },

  /**
   * Clock in or out (smart toggle)
   */
  async attendance_action_clock__validates_all__learns_patterns(
    token: string,
    data: ClockRequest,
  ): Promise<ClockResponse> {
    // Per DD-0002: branch_id from caller, fallback to /tenant-users/me API
    let branchId = data.branchId;
    if (!branchId && token) {
      try {
        const me = await apiRequest("/tenant-users/me", { method: "GET", token });
        branchId = me?.branch_id;
      } catch { /* ignore — will fail with missing branch_id */ }
    }
    const payload: any = {
      latitude: data.location.lat,
      longitude: data.location.lon,
      branch_id: branchId,
    };

    // Only add timestamp if provided
    if (data.timestamp) {
      payload.timestamp = data.timestamp;
    }

    // Add location status if provided
    if (data.locationStatus) {
      payload.location_status = data.locationStatus;
    }

    // BR-ATTN-001 Scenario 5: Add photo if captured (Base64 JPEG)
    if (data.photo) {
      payload.photo = data.photo;
    }

    try {
      // Per gps-attendance-api.yaml: GPS clock-in endpoint under /gps-attendance prefix
      const response = await apiRequest("/gps-attendance/attendance/clock-in", {
        method: "POST",
        body: JSON.stringify(payload),
        token,
      });

      return response as ClockResponse;
    } catch (_error) {
      throw _error;
    }
  },

  /**
   * Clock in explicitly
   * Transforms frontend location format to backend expected format
   */
  async clockIn(token: string, data: ClockRequest): Promise<ClockResponse> {
    const payload = {
      latitude: data.location.lat,
      longitude: data.location.lon,
      accuracy: GPSConfig.DEFAULT_ACCURACY,
      notes: data.notes || "",
    };
    return apiRequest("/attendance/clock-in", {
      method: "POST",
      body: JSON.stringify(payload),
      token,
    });
  },

  /**
   * Clock out explicitly
   * Transforms frontend location format to backend expected format
   */
  async clockOut(token: string, data: ClockRequest): Promise<ClockResponse> {
    const payload = {
      latitude: data.location.lat,
      longitude: data.location.lon,
      accuracy: GPSConfig.DEFAULT_ACCURACY,
      notes: data.notes || "",
    };
    return apiRequest("/attendance/clock-out", {
      method: "POST",
      body: JSON.stringify(payload),
      token,
    });
  },

  /**
   * Get team attendance (for managers)
   */
  async getTeamAttendance(token: string): Promise<any> {
    return apiRequest("/attendance/team", {
      method: "GET",
      token,
    });
  },

  // ── Timemark API (v2) — BR-ATTN-001 Scenario 6, ATTENDANCE-MULTI-METHOD §3.3 ──

  /**
   * Submit timemark clock-in (last-resort fallback, requires manager approval)
   * POST /api/v2/attendance/timemark-clock-in
   */
  async submitTimemark(
    token: string,
    data: {
      reason?: string;
      fallback_chain?: Array<{ method: string; status: string; timestamp?: string }>;
      /** SC-017-EMP-ATTENDANCE [6] amend (BS-004-R15, 2026-09-02) — ảnh xác nhận tuỳ chọn. */
      photo?: string;
      /** SC-017-EMP-ATTENDANCE [6] amend (BS-004-R15, 2026-09-02) — GPS tuỳ chọn, lấy ngầm. */
      latitude?: number;
      longitude?: number;
    },
  ): Promise<{
    attendance_id: string;
    status: string;
    approval_deadline: string;
    manager_notified: boolean;
    message_vi: string;
  }> {
    const response = await apiV2.post("/attendance/timemark-clock-in", data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Get pending timemark approvals (manager view)
   * GET /api/v2/attendance/timemark-approvals (if available) or list from team
   */
  async getPendingTimemarks(token: string): Promise<any[]> {
    const response = await apiV2.get("/attendance/timemark-approvals", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Approve or reject a timemark clock-in (manager action)
   * POST /api/v2/attendance/{attendance_id}/timemark-approval
   */
  async reviewTimemark(
    token: string,
    attendanceId: string,
    data: { action: "APPROVE" | "REJECT"; manager_notes?: string },
  ): Promise<{
    attendance_id: string;
    status: string;
    approved_by?: string;
    approved_at?: string;
    message_vi: string;
  }> {
    const response = await apiV2.post(`/attendance/${attendanceId}/timemark-approval`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // ==========================================================================
  // SC-017-EMP-ATTENDANCE [8] checklist (2026-09-02) — 3 hàm mới, web trước đây
  // KHÔNG có tích hợp nào với backend/app/api/v2/endpoints/attendance_sprint72.py.
  // ==========================================================================

  /** POST /api/v2/attendance/{attendance_id}/activate-checklist?shift_type=... */
  async activateChecklist(
    token: string,
    attendanceId: string,
    shiftType: "PRE_SHIFT" | "POST_SHIFT",
  ): Promise<ChecklistActivateResponse> {
    const response = await apiV2.post(
      `/attendance/${attendanceId}/activate-checklist`,
      {},
      { params: { shift_type: shiftType }, headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  /**
   * GET /api/v2/attendance/{attendance_id}/checklist-status?shift_type=...
   * Trả `null` khi chưa activate (backend 404 "Không có checklist cho bản ghi
   * chấm công này." — KHÔNG phải lỗi, là trạng thái "chưa kích hoạt" hợp lệ).
   */
  async getChecklistStatus(
    token: string,
    attendanceId: string,
    shiftType: "PRE_SHIFT" | "POST_SHIFT",
  ): Promise<ChecklistStatusResponse | null> {
    try {
      const response = await apiV2.get(`/attendance/${attendanceId}/checklist-status`, {
        params: { shift_type: shiftType },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  /** POST /api/v2/attendance/checklist-items/{instance_id}/tick */
  async tickChecklistItem(
    token: string,
    instanceId: string,
    data: { item_id: string; is_checked: boolean },
  ): Promise<ChecklistTickResponse> {
    const response = await apiV2.post(`/attendance/checklist-items/${instanceId}/tick`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// SC-017-EMP-ATTENDANCE [8] checklist (2026-09-02) — khớp
// backend/app/schemas/v2/attendance_sprint72.py
export interface ChecklistItem {
  item_id: string;
  title_vi: string;
  is_mandatory: boolean;
  requires_photo: boolean;
  requires_note: boolean;
  is_checked: boolean;
  checked_at: string | null;
  note: string | null;
  photo_url: string | null;
}

export interface ChecklistProgress {
  total: number;
  completed: number;
  mandatory_total: number;
  mandatory_completed: number;
  percentage: number;
}

export interface ChecklistStatusResponse {
  instance_id: string;
  shift_type: string;
  status: string;
  progress: ChecklistProgress;
  grace_period_minutes: number;
  is_overdue: boolean;
  items: ChecklistItem[];
}

export interface ChecklistActivateResponse {
  instance_id: string;
  template_name: string;
  shift_type: string;
  status: string;
  total_items: number;
  completed_items: number;
  mandatory_items: number;
  mandatory_completed: number;
  grace_period_minutes: number;
  is_overdue: boolean;
}

export interface ChecklistTickResponse {
  item_id: string;
  is_checked: boolean;
  checked_at: string | null;
  instance_status: string;
  progress: ChecklistProgress;
}
